import { useState } from 'react';

import { api } from '../api/client';
import { Badge, Button, Card, Field, Form, Loading, Notice, Select, Table } from '../components';
import { useAction, useResource } from '../hooks';
import {
  EMPTY_DRAFT,
  QuestionSettings,
  QuestionSummary,
  draftOf,
  type QuestionDraft,
} from './QuestionSettings';

/**
 * Questions written once, used anywhere.
 *
 * The point of this screen is the sentence in the notice: **inserting takes a copy**. Nothing
 * here reaches back into a form that already used an entry, and that is deliberate rather
 * than a limitation. Forms are versioned so a recorded answer keeps meaning what it meant
 * when it was given; a central edit that rewrote published questionnaires would walk straight
 * around that, and nobody would notice until they tried to explain an old verdict.
 *
 * So the screen says so where an author will read it, not only in the code.
 */

const QUESTION_TYPES = [
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'BOOLEAN', label: 'Yes / No' },
  { value: 'SINGLE_CHOICE', label: 'Choose one' },
  { value: 'MULTI_CHOICE', label: 'Choose several' },
  { value: 'DATE', label: 'Date' },
  { value: 'PHOTO', label: 'Photograph' },
  { value: 'DOCUMENT', label: 'Document' },
  { value: 'SIGNATURE', label: 'Signature' },
];

const CHOICE_TYPES = new Set(['BOOLEAN', 'SINGLE_CHOICE', 'MULTI_CHOICE']);

interface LibraryNode {
  id: string;
  category: string | null;
  prompt: string;
  helpText: string | null;
  stepType: string;
  displayOrder: number;
  isRequired: boolean;
  requiresEvidence: boolean;
  cameraFacing: string;
  dateMode: string;
  minDateMode: string;
  maxDateMode: string;
  minDate: string | null;
  maxDate: string | null;
  minValue: string | null;
  maxValue: string | null;
  parentStepId: string | null;
  parentOptionId: string | null;
  children: LibraryNode[];
  options: Array<{
    id: string;
    value: string;
    label: string;
    displayOrder: number;
    children: LibraryNode[];
  }>;
}

export function QuestionLibrary(): JSX.Element {
  const library = useResource(() => api.get<LibraryNode[]>('/library/questions'), []);
  const { run, error, busy } = useAction();

  const [category, setCategory] = useState('');
  const [stepType, setStepType] = useState('TEXT');
  const [draft, setDraft] = useState<QuestionDraft>(EMPTY_DRAFT);
  const [addingUnder, setAddingUnder] = useState<{ questionId: string; optionId: string } | null>(
    null,
  );
  /** Which entry is open for editing, and its working copy. */
  const [editing, setEditing] = useState<{
    id: string;
    stepType: string;
    draft: QuestionDraft;
  } | null>(null);

  /** The per-type fields the server takes, narrowed to the ones this type actually uses. */
  const settingsFor = (type: string, values: QuestionDraft): Record<string, unknown> => ({
    helpText: values.helpText,
    isRequired: values.isRequired,
    requiresEvidence: values.requiresEvidence,
    ...(type === 'PHOTO' ? { cameraFacing: values.cameraFacing } : {}),
    ...(type === 'DATE'
      ? {
          dateMode: values.dateMode,
          minDateMode: values.minDateMode,
          maxDateMode: values.maxDateMode,
          minDate: values.minDate === '' ? null : values.minDate,
          maxDate: values.maxDate === '' ? null : values.maxDate,
        }
      : {}),
  });

  const add = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.post('/library/questions', {
        prompt: draft.prompt,
        stepType,
        ...(category === '' ? {} : { category }),
        ...settingsFor(stepType, draft),
      });
    });

    if (ok) {
      setDraft(EMPTY_DRAFT);
      library.reload();
    }
  };

  const saveEdit = async (): Promise<void> => {
    if (editing === null) return;

    const ok = await run(async () => {
      await api.patch(`/library/questions/${editing.id}`, {
        prompt: editing.draft.prompt,
        ...settingsFor(editing.stepType, editing.draft),
      });
    });

    if (ok) {
      setEditing(null);
      library.reload();
    }
  };

  const addFollowUp = async (
    followUpType: string,
    followUpDraft: QuestionDraft,
  ): Promise<void> => {
    if (addingUnder === null) return;

    const ok = await run(async () => {
      await api.post('/library/questions', {
        prompt: followUpDraft.prompt,
        stepType: followUpType,
        parentLibraryQuestionId: addingUnder.questionId,
        parentOptionId: addingUnder.optionId,
        ...settingsFor(followUpType, followUpDraft),
      });
    });

    if (ok) {
      setAddingUnder(null);
      library.reload();
    }
  };

  const remove = async (id: string): Promise<void> => {
    const ok = await run(async () => {
      await api.delete(`/library/questions/${id}`);
    });
    if (ok) library.reload();
  };

  return (
    <>
      <Card title="Question library">
        <Notice kind="info">
          Inserting a library question into a form takes a <strong>copy</strong>. Editing it
          here afterwards does not change forms that already use it — that is what keeps an
          answer meaning what the question meant when it was asked.
        </Notice>

        {error !== null && <Notice kind="error">{error}</Notice>}

        {library.loading ? (
          <Loading />
        ) : library.error !== null ? (
          <Notice kind="error">{library.error}</Notice>
        ) : (
          <Table
            rows={library.data ?? []}
            keyOf={(row) => row.id}
            empty="Nothing in the library yet. Add a question below."
            columns={[
              { header: 'Category', render: (row) => row.category ?? '—' },
              { header: 'Question', render: (row) => row.prompt },
              {
                header: 'Type',
                render: (row) => (
                  <Badge tone="low">
                    {QUESTION_TYPES.find((type) => type.value === row.stepType)?.label ??
                      row.stepType}
                  </Badge>
                ),
              },
              {
                header: 'Follow-ups',
                numeric: true,
                render: (row) =>
                  row.options.reduce((total, option) => total + option.children.length, 0),
              },
              {
                header: '',
                render: (row) => (
                  <div className="row">
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() =>
                        setEditing({ id: row.id, stepType: row.stepType, draft: draftOf(row) })
                      }
                    >
                      Edit
                    </Button>
                    <Button variant="danger" disabled={busy} onClick={() => void remove(row.id)}>
                      Delete
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      {editing !== null && (
        <Card title="Edit library question">
          <Notice kind="info">
            This changes the library only. Forms that already used this question keep the copy
            they took.
          </Notice>

          <Form onSubmit={() => void saveEdit()}>
            <QuestionSettings
              stepType={editing.stepType}
              draft={editing.draft}
              onChange={(next) => setEditing({ ...editing, draft: next })}
              promptLabel="Question"
            />
            <div className="form-actions">
              <Button type="submit" disabled={busy || editing.draft.prompt === ''}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          </Form>
        </Card>
      )}

      {/* Follow-ups per answer, exactly as on a form — a library entry that could not carry
          its branching would leave the author to rebuild the only part worth reusing. */}
      {(library.data ?? [])
        .filter((entry) => CHOICE_TYPES.has(entry.stepType))
        .map((entry) => (
          <Card key={entry.id} title={entry.prompt}>
            {entry.options.map((option) => (
              <div key={option.id} className="option-block">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <strong>{option.label}</strong>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setAddingUnder(
                        addingUnder?.optionId === option.id
                          ? null
                          : { questionId: entry.id, optionId: option.id },
                      )
                    }
                  >
                    {addingUnder?.optionId === option.id ? 'Cancel' : '+ Follow-up question'}
                  </Button>
                </div>

                {option.children.map((child) => (
                  <div key={child.id} className="row" style={{ marginLeft: 22, gap: 8 }}>
                    <span className="muted">↳ {child.prompt}</span>
                    <Badge tone="low">
                      {QUESTION_TYPES.find((type) => type.value === child.stepType)?.label ??
                        child.stepType}
                    </Badge>
                    <QuestionSummary question={child} />
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() =>
                        setEditing({
                          id: child.id,
                          stepType: child.stepType,
                          draft: draftOf(child),
                        })
                      }
                    >
                      Edit
                    </Button>
                    <Button variant="danger" disabled={busy} onClick={() => void remove(child.id)}>
                      Delete
                    </Button>
                  </div>
                ))}

                {addingUnder?.optionId === option.id && (
                  <FollowUpForm busy={busy} onAdd={addFollowUp} />
                )}
              </div>
            ))}
          </Card>
        ))}

      <Card title="Add to the library">
        <Form onSubmit={() => void add()}>
          <div className="form-row">
            <Field
              label="Category"
              value={category}
              onChange={setCategory}
              hint="Optional, for grouping in the picker — e.g. Safety."
            />
            <Select
              label="Answer type"
              value={stepType}
              onChange={setStepType}
              required
              options={QUESTION_TYPES}
            />
          </div>

          {/* The same editor the form screen uses. This form used to take a prompt and a
              type and nothing else, so a Photograph entry could not say which camera and
              every copy made from it silently defaulted. */}
          <QuestionSettings
            stepType={stepType}
            draft={draft}
            onChange={setDraft}
            promptLabel="Question"
          />

          <div className="form-actions">
            <Button type="submit" disabled={busy || draft.prompt === ''}>
              {busy ? 'Adding…' : 'Add question'}
            </Button>
          </div>
        </Form>
      </Card>
    </>
  );
}

function FollowUpForm({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (stepType: string, draft: QuestionDraft) => Promise<void>;
}): JSX.Element {
  const [stepType, setStepType] = useState('TEXT');
  const [draft, setDraft] = useState<QuestionDraft>(EMPTY_DRAFT);

  return (
    <div style={{ marginLeft: 22 }}>
      <Form onSubmit={() => void onAdd(stepType, draft)}>
        <div className="form-row">
          <Select
            label="Answer type"
            value={stepType}
            onChange={setStepType}
            required
            options={QUESTION_TYPES}
          />
        </div>

        <QuestionSettings
          stepType={stepType}
          draft={draft}
          onChange={setDraft}
          promptLabel="Follow-up question"
        />

        <div className="form-actions">
          <Button type="submit" disabled={busy || draft.prompt === ''}>
            Add follow-up
          </Button>
        </div>
      </Form>
    </div>
  );
}
